/**
 * Unified Accessibility Editor — the typed upload routing seam.
 *
 * Phase 2 of docs/epics/active/unified-accessibility-editor-260715/main.md
 * (design: docs/design/unified-accessibility-editor-260715.md §T.1/§T.3/§3.2/§3.3).
 *
 * ONE typed, closed routing contract for the unified `/editor` upload entry. A
 * dropped/browsed file is type-dispatched by extension → an `EditorRouteEntry`
 * carrying the served `surface`, the format's `capabilityTier`, the format's
 * `PrivacyClaim`, and — the piece the DIRECT-UPLOAD entry needs — a
 * `directUploadAction` telling the empty-state wiring what to DO with a fresh
 * (jobId-less) file of that format:
 *
 *   - `'wasm-load'`      — PDF: register a transient job + load the WASM editor
 *                          shell in place (the existing `loadFile` chokepoint).
 *   - `'pipeline-upload'`— Office (.pptx/.docx): this surface has no direct Office
 *                          backend (the PDF-only `/api/editor-transient` rejects
 *                          non-PDF). A fresh Office file must go through the main
 *                          pipeline (`/`) to be remediated before `/o365-view` can
 *                          render it. The dispatch routes the user there honestly
 *                          (never a silent no-op — rule #17). Post-remediation the
 *                          job-card "Editor" button (renderers.ts) opens the
 *                          `surface`-correct review view via `hrefFor(jobId)`.
 *   - `'reject-not-yet'` — XLSX (§G-Q2 = accept + honest "not yet"): recognized,
 *                          explained, NOT silently dropped.
 *
 * `hrefFor(jobId)` is the jobId-keyed review-view URL for a format that HAS a
 * backend-produced job (the job-card path). It is the same `surface`→URL contract
 * the SPA job-card map (`renderers.ts`) uses; kept structurally identical here so
 * the two seams stay in lockstep. (A single shared module is blocked today by the
 * two trees' isolated tsconfigs — see the [FIX] follow-up in the Epic report.)
 *
 * §3.3 rewire-ready property: the platform `doc-edit-service` rewire is a VALUE
 * FLIP of `EditorRouteEntry.surface` (already includes the forward-declared
 * `'doc-edit-service'` member) + `hrefFor`; the dispatch/UX reads `surface` +
 * `capabilityTier`, so none of it changes at the rewire.
 */
const _pdfHref = (id) => `/editor?jobId=${encodeURIComponent(id)}`;
const _o365Href = (fmt) => (id) => `/o365-view?jobId=${encodeURIComponent(id)}&format=${fmt}`;
/**
 * The SINGLE routing source of truth for the unified editor upload (§3.2). A
 * frozen closed map keyed by `SupportedUploadExt`. Supersedes the ad-hoc "only
 * PDF" gate the empty-state wiring used to carry.
 */
export const EDITOR_ROUTE_BY_EXT = Object.freeze({
    ".pdf": {
        ext: ".pdf",
        surface: "wasm-pdf",
        capabilityTier: "ui-full",
        privacyClaim: "browser-only",
        directUploadAction: "wasm-load",
        hrefFor: _pdfHref,
        label: "PDF",
        iconId: "icon-pdf",
        emptyStateFact: "uploaded to our server for editing; pages render locally in your browser.",
    },
    ".pptx": {
        ext: ".pptx",
        surface: "a11y-view-office",
        capabilityTier: "ui-limited",
        privacyClaim: "server-processed",
        directUploadAction: "pipeline-upload",
        hrefFor: _o365Href("pptx"),
        label: "PowerPoint",
        iconId: "icon-pptx",
        emptyStateFact: "reviewed on our server; alt text and decorative marking are editable.",
    },
    ".docx": {
        ext: ".docx",
        surface: "a11y-view-office",
        capabilityTier: "ui-limited",
        privacyClaim: "server-processed",
        directUploadAction: "pipeline-upload",
        hrefFor: _o365Href("docx"),
        label: "Word",
        iconId: "icon-docx",
        emptyStateFact: "reviewed on our server; alt text and decorative marking are editable.",
    },
    ".xlsx": {
        ext: ".xlsx",
        surface: "a11y-view-office",
        capabilityTier: "none",
        privacyClaim: "server-processed",
        directUploadAction: "reject-not-yet",
        hrefFor: () => null,
        label: "Excel",
        iconId: "icon-xlsx",
        emptyStateFact: "reviewed on our server; alt text and decorative marking are editable.",
    },
    ".odp": {
        ext: ".odp",
        surface: "a11y-view-office",
        capabilityTier: "ui-limited",
        privacyClaim: "server-processed",
        directUploadAction: "odp-passthrough-load",
        hrefFor: _o365Href("odp"),
        label: "OpenDocument Presentation",
        iconId: "icon-odp",
        emptyStateFact: "reviewed on our server; alt text and decorative marking are editable (ODF Impress, like PowerPoint).",
    },
});
/** The `accept` attribute string derived from the supported set (§3.1). */
export const UPLOAD_ACCEPT_ATTR = Object.keys(EDITOR_ROUTE_BY_EXT).join(",");
/** The lowercase extension of a filename (including the dot), or `null`. */
export function extOf(filename) {
    const name = (filename ?? "").toLowerCase();
    const dot = name.lastIndexOf(".");
    if (dot < 0)
        return null;
    return name.slice(dot);
}
/** The route entry for a supported extension, or `null` when unsupported. */
export function routeForExt(ext) {
    if (ext === null)
        return null;
    return (ext in EDITOR_ROUTE_BY_EXT)
        ? EDITOR_ROUTE_BY_EXT[ext]
        : null;
}
/**
 * Type-dispatch a file by extension → the routing decision. Never throws; an
 * unsupported extension yields `{ kind: 'unsupported' }` so the caller surfaces an
 * accessible message (rule #17 post-condition — never a silent no-op).
 */
export function dispatchUpload(file) {
    const ext = extOf(file.name);
    const entry = routeForExt(ext);
    if (entry === null)
        return { kind: "unsupported", ext };
    return { kind: "route", ext: entry.ext, entry };
}
